import React, { forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { rgba, darken } from "polished";

import { Chip, Collapse, ListItemButton, ListItemText } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import { getBadgeCount } from "../../redux/slices/counter";
import SidebarContext, { useSidebarFade } from "./SidebarContext";

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref}>
    <NavLink {...props} />
  </div>
));

// A top-level item's left padding centres its icon in the sidebar's icon
// bar (RAIL_WIDTH), so the icons stay put as the sidebar opens.
const Item = styled(ListItemButton)`
  padding-top: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-left: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 11.5 : 5.5)};
  padding-right: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 4 : 7)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }
  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: ${(props) => props.theme.sidebar.color};
  }
  &.${(props) => props.activeclassname} {
    background-color: ${(props) =>
      darken(0.03, props.theme.sidebar.background)};
    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`;

const Title = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) =>
      rgba(
        props.theme.sidebar.color,
        props.depth && props.depth > 0 ? 0.7 : 1
      )};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
    white-space: nowrap;
  }
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: 1;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const SidebarNavListItem = (props) => {
  const dispatch = useDispatch();
  const counts = useSelector((state) => state.counter);
  const {
    title,
    href,
    depth = 0,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
    dataLocation,
    countChild,
    countTerm,
    current = false,
  } = props;
  const { collapsed } = React.useContext(SidebarContext);
  const fade = useSidebarFade();

  const [open, setOpen] = React.useState(openProp);
  const [badgeLabel, setBadgeLabel] = React.useState(null);

  const handleToggle = () => {
    setOpen((state) => !state);
  };
  React.useEffect(() => {
    if (typeof badge !== "function") return;
    const label = badge(counts[dataLocation]);
    setBadgeLabel(label);
  }, [badge, counts]);
  React.useEffect(() => {
    if (!dataLocation) return;
    dispatch(getBadgeCount({ dataLocation, countChild, countTerm }));
  }, [dispatch]);
  if (children) {
    return (
      <React.Fragment>
        <Item
          depth={depth}
          onClick={handleToggle}
          // On the icon bar, where its list is closed, the item holding
          // the page shows as the current one.
          sx={
            collapsed && current
              ? (theme) => ({
                  backgroundColor: darken(0.03, theme.sidebar.background),
                })
              : undefined
          }
        >
          {Icon && <Icon />}
          <Title depth={depth} sx={fade}>
            {title}
            {badge && <Badge label={badge} />}
          </Title>
          {open ? <ExpandLessIcon sx={fade} /> : <ExpandMoreIcon sx={fade} />}
        </Item>
        <Collapse in={open && !collapsed}>{children}</Collapse>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Item
        depth={depth}
        component={CustomRouterLink}
        to={href}
        activeclassname="active"
      >
        {Icon && <Icon />}
        <Title depth={depth} sx={fade}>
          {title}
          {badge && (
            <Badge label={typeof badge === "function" ? badgeLabel : badge} />
          )}
        </Title>
      </Item>
    </React.Fragment>
  );
};

export default SidebarNavListItem;
